import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Settings, ArrowLeft, Store, ShoppingBag, User, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'buyer'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        user_type: 'buyer'
      });
      
      toast.success('Kayıt başarılı!');
      
      // Satıcı seçildiyse başvuru formuna yönlendir
      if (formData.userType === 'seller') {
        setTimeout(() => {
          toast.info('Şimdi firma bilgilerinizi girin');
          navigate('/seller/apply');
        }, 500);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="register-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-orange-500 to-orange-600 items-center justify-center p-8">
        <div className="text-center text-white max-w-md">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Settings className="h-10 w-10" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Pazarlo'e Katılın</h2>
          <p className="text-orange-100 text-lg mb-8">
            Binlerce ürün, yüzlerce satıcı arasından seçim yapın veya kendi mağazanızı açarak satışa başlayın.
          </p>
          <div className="grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/10 rounded-xl p-4">
              <User className="h-8 w-8 mb-2" />
              <h3 className="font-semibold">Alıcı Olun</h3>
              <p className="text-sm text-orange-100">En iyi fiyatlarla yedek parça alın</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <Store className="h-8 w-8 mb-2" />
              <h3 className="font-semibold">Satıcı Olun</h3>
              <p className="text-sm text-orange-100">Mağaza açın, ürünlerinizi satın</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Ana Sayfaya Dön
          </Link>

          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Hesap Oluştur</CardTitle>
              <CardDescription>Alışverişe hemen başlayın</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* User Type Selection */}
                <div>
                  <Label className="mb-3 block">Ne yapmak istiyorsunuz?</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, userType: 'buyer' })}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all ${
                        formData.userType === 'buyer' 
                          ? 'border-orange-500 bg-orange-50 shadow-md' 
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                      data-testid="buyer-type-btn"
                    >
                      <ShoppingBag className={`h-8 w-8 mb-2 ${formData.userType === 'buyer' ? 'text-orange-600' : 'text-slate-500'}`} />
                      <span className="font-medium">Alıcı</span>
                      <span className="text-xs text-slate-500">Ürün satın al</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, userType: 'seller' })}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 p-4 transition-all ${
                        formData.userType === 'seller' 
                          ? 'border-orange-500 bg-orange-50 shadow-md' 
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                      data-testid="seller-type-btn"
                    >
                      <Store className={`h-8 w-8 mb-2 ${formData.userType === 'seller' ? 'text-orange-600' : 'text-slate-500'}`} />
                      <span className="font-medium">Satıcı</span>
                      <span className="text-xs text-slate-500">Mağaza aç</span>
                    </button>
                  </div>
                </div>

                {/* Satıcı bilgi kutusu */}
                {formData.userType === 'seller' && (
                  <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-amber-800 mb-1">Satıcı Başvurusu - 2 Adım</p>
                        <ol className="text-sm text-amber-700 space-y-1">
                          <li><span className="font-semibold">1.</span> Bu formu doldurun (kişisel bilgiler)</li>
                          <li><span className="font-semibold">2.</span> Sonraki sayfada firma bilgilerini girin</li>
                        </ol>
                        <p className="text-xs text-amber-600 mt-2">* Admin onayından sonra mağazanız açılır</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="name">Ad Soyad *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Adınız Soyadınız"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">E-posta *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="05XX XXX XX XX"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Şifre *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="En az 6 karakter"
                      value={formData.password}
                      onChange={handleChange}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Şifre Tekrar *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Şifrenizi tekrar girin"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>

                <div className="text-sm text-slate-500">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" className="rounded mt-1" required />
                    <span>
                      <Link to="/terms" className="text-orange-500 hover:text-orange-600">Kullanım Koşulları</Link>
                      {' '}ve{' '}
                      <Link to="/privacy" className="text-orange-500 hover:text-orange-600">Gizlilik Politikası</Link>
                      'nı okudum ve kabul ediyorum.
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  className={`w-full ${formData.userType === 'seller' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600'}`}
                  disabled={loading}
                  data-testid="register-btn"
                >
                  {loading ? (
                    'Kayıt yapılıyor...'
                  ) : formData.userType === 'seller' ? (
                    <span className="flex items-center justify-center gap-2">
                      Devam Et - Firma Bilgileri <ArrowRight className="w-4 h-4" />
                    </span>
                  ) : (
                    'Kayıt Ol'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-500">
                Zaten hesabınız var mı?{' '}
                <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">
                  Giriş Yap
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
